/**
 * V4ThemePlayground.tsx  
 * Comprehensive showcase of Tailwind v4 theme system
 * 
 * Uses our shared UI components to demonstrate the theme in action
 */

import React from 'react';
import { PlaygroundPageLayout } from '../shared';
import { Tabs, Tab } from '@enfomentis/ui';
import { 
  Eye, 
  Palette, 
  Layers, 
  Paintbrush, 
  Grid3x3, 
  Brush, 
  Type, 
  Zap 
} from 'lucide-react';

// Section components
import { OverviewSection } from './sections/OverviewSection';
import { HeroUIColorsSection } from './sections/HeroUIColorsSection';
import { ColorScalesSection } from './sections/ColorScalesSection';
import { UtilityColorsSection } from './sections/UtilityColorsSection';
import { ComponentMatrixSection } from './sections/ComponentMatrixSection';
import { ColorUtilitiesSection } from './sections/ColorUtilitiesSection';
import { TypographySection } from './sections/TypographySection';
import { InteractiveSection } from './sections/InteractiveSection';


export const V4ThemePlayground: React.FC = () => {
  return (
    <PlaygroundPageLayout>
      <div className="mt-8">
        <Tabs variant="bordered" color="secondary" size="md" fullWidth>
          
          {/* Overview Tab */}
          <Tab 
            key="overview" 
            title={
              <div className="flex items-center gap-2">
                <Eye size={16} />
                <span>Overview</span>
              </div>
            }
          >
            <div className="py-6">
              <OverviewSection />
            </div>
          </Tab>

          {/* HeroUI Colors Tab */}
          <Tab 
            key="heroui-colors" 
            title={
              <div className="flex items-center gap-2">
                <Palette size={16} />
                <span>HeroUI Colors</span>
              </div>
            }
          >
            <div className="py-6">
              <HeroUIColorsSection />
            </div>
          </Tab>

          {/* Semantic Colors Tab */}
          <Tab 
            key="semantic-colors" 
            title={
              <div className="flex items-center gap-2">
                <Layers size={16} />
                <span>Semantic Colors</span>
              </div>
            }
          >
            <div className="py-6">
              <ColorScalesSection />
            </div>
          </Tab>

          {/* Utility Colors Tab */}
          <Tab 
            key="utility-colors" 
            title={
              <div className="flex items-center gap-2">
                <Paintbrush size={16} />
                <span>Utility Colors</span>
              </div>
            }
          >
            <div className="py-6">
              <UtilityColorsSection />
            </div>
          </Tab>

          {/* Component Matrix Tab */}
          <Tab 
            key="component-matrix" 
            title={
              <div className="flex items-center gap-2">
                <Grid3x3 size={16} />
                <span>Component Matrix</span>
              </div>
            }
          >
            <div className="py-6">
              <ComponentMatrixSection />
            </div>
          </Tab>

          {/* Color Utilities Tab */}
          <Tab 
            key="color-utilities" 
            title={
              <div className="flex items-center gap-2">
                <Brush size={16} />
                <span>Color Utilities</span>
              </div>
            }
          >
            <div className="py-6">
              <ColorUtilitiesSection />
            </div>
          </Tab>

          {/* Typography Tab */}
          <Tab 
            key="typography" 
            title={
              <div className="flex items-center gap-2">
                <Type size={16} />
                <span>Typography</span>
              </div>
            }
          >
            <div className="py-6">
              <TypographySection />
            </div>
          </Tab>

          {/* Interactive Tab */}
          <Tab 
            key="interactive" 
            title={
              <div className="flex items-center gap-2">
                <Zap size={16} />
                <span>Interactive</span>
              </div>
            }
          >
            <div className="py-6">
              <InteractiveSection onBackToOverview={() => {}} />
            </div>
          </Tab>
          
        </Tabs>
      </div>
    </PlaygroundPageLayout>
  );
};